import { useCallback, useEffect, useState } from "react";

const BANKS = ["Chase", "Wells Fargo", "Bank of America", "Capital One", "US Bank", "Citi", "Other"];

// Linked funding — the member's bank card lives in their wallet as a funding instrument.
// No card number, no third-party account: the bank itself moves the money (Zelle/wire from
// their own banking app), the desk confirms, the wallet credits 1:1. Apple-Wallet simple.
export const WalletFundingSources = ({ api, token, onLoaded, merchantApproved }) => {
  const [sources, setSources] = useState(null);
  const [instructions, setInstructions] = useState(null);
  const [adding, setAdding] = useState(false);
  const [bank, setBank] = useState("Chase");
  const [customBank, setCustomBank] = useState("");
  const [last4, setLast4] = useState("");
  const [rail, setRail] = useState("ZELLE");
  const [amounts, setAmounts] = useState({});
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [memo, setMemo] = useState(null); // {code, amount, rail} — the freshly minted load memo

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [s, f] = await Promise.all([
        fetch(`${api}/wallet/funding-sources`, { headers }),
        fetch(`${api}/wallet/wire-instructions`, { headers }),
      ]);
      if (s.ok) setSources((await s.json()).sources || []);
      if (f.ok) setInstructions(await f.json());
    } catch { /* funding sources are additive */ }
  }, [api, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (token) load(); }, [token, load]);

  // watch a fresh memo code: when the desk settles, the wallet view refreshes on its own
  useEffect(() => {
    if (!memo) return;
    const t = setInterval(async () => {
      try {
        const res = await fetch(`${api}/wallet/funding`, { headers });
        if (res.ok) {
          const d = await res.json();
          const mine = (d.intentions || []).find((i) => i.intention_id === memo.code);
          if (mine && mine.status === "SETTLED") {
            setMemo(null);
            setMsg("Settled — the desk confirmed it and the money is in your wallet.");
            if (onLoaded) onLoaded();
          }
        }
      } catch { /* keep watching */ }
    }, 8000);
    return () => clearInterval(t);
  }, [memo, api, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const addSource = async (e) => {
    e.preventDefault();
    setBusy("add");
    setMsg("");
    try {
      const label = bank === "Other" ? customBank.trim() : bank;
      const res = await fetch(`${api}/wallet/funding-sources`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ bank_label: label, last4: last4 || null, rail }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not link it");
      setAdding(false);
      setLast4("");
      setCustomBank("");
      setMsg(`${d.bank_label}${d.last4 ? " •••• " + d.last4 : ""} linked — loads from it land by ${d.rail === "ZELLE" ? "Zelle" : "wire"}.`);
      load();
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy("");
    }
  };

  const startLoad = async (s) => {
    const amount = parseFloat(amounts[s.source_id] || "0");
    if (!amount || amount <= 0) { setMsg("Enter an amount first."); return; }
    setBusy(s.source_id);
    setMsg("");
    try {
      const res = await fetch(`${api}/wallet/funding-intention`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ amount_usd: amount, rail: s.rail }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not open the request");
      setMemo({ code: d.intention_id, amount, rail: s.rail, bank: s.bank_label });
      setAmounts({ ...amounts, [s.source_id]: "" });
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy("");
    }
  };

  // money OUT: the desk sends it by Zelle to her own handle; her wallet debits on-chain first
  const startCashOut = async (s) => {
    const amount = parseFloat(amounts[s.source_id] || "0");
    if (!amount || amount <= 0) { setMsg("Enter an amount first."); return; }
    setBusy(s.source_id + "-out");
    setMsg("");
    try {
      const res = await fetch(`${api}/wallet/wireout-request`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          amount_usd: amount,
          bank_label: `${s.bank_label}${s.last4 ? " ••••" + s.last4 : ""}`,
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not queue the cash-out");
      setMsg(`Cash-out queued — ref ${d.request_id}. The desk is texting you when the Zelle is on its way to your phone.`);
      setAmounts({ ...amounts, [s.source_id]: "" });
      if (onLoaded) onLoaded();
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy("");
    }
  };

  const removeSource = async (id) => {
    await fetch(`${api}/wallet/funding-sources/${id}`, { method: "DELETE", headers });
    load();
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch { /* clipboard optional */ }
  };

  return (
    <div data-testid="wallet-funding-sources" className="rounded-xl border border-stone-700 bg-stone-900/60 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-mono uppercase tracking-widest text-amber-500/80">Linked funding</p>
        <p className="text-[9px] font-mono text-stone-500">your bank moves it · desk attests</p>
      </div>

      {sources && sources.length > 0 && (
        <div className="space-y-2">
          {sources.map((s) => (
            <div key={s.source_id} data-testid={`funding-source-${s.source_id}`}
                 className="rounded-lg border border-stone-800 bg-stone-950/60 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-stone-100">
                  {s.bank_label} {s.last4 && <span className="font-mono">•••• {s.last4}</span>}
                  <span className="ml-2 text-[9px] font-mono text-emerald-400/80 uppercase">{s.rail}</span>
                </p>
                <button onClick={() => removeSource(s.source_id)} data-testid={`source-remove-${s.source_id}`}
                        className="text-[10px] font-mono text-stone-500 hover:text-red-400">remove</button>
              </div>
              <div className="flex gap-2">
                <input type="number" min="1" step="0.01" placeholder="$ amount"
                       value={amounts[s.source_id] || ""}
                       onChange={(e) => setAmounts({ ...amounts, [s.source_id]: e.target.value })}
                       data-testid={`source-load-amount-${s.source_id}`}
                       className="flex-1 rounded-lg border border-stone-700 bg-stone-950/70 px-3 py-2 text-sm text-stone-100" />
                <button onClick={() => startLoad(s)} disabled={busy === s.source_id}
                        data-testid={`source-load-button-${s.source_id}`} title="Move money IN from your bank"
                        className="rounded-lg bg-emerald-500 px-4 text-xs font-bold uppercase tracking-widest text-stone-950 disabled:opacity-40">
                  {busy === s.source_id ? "…" : "Add"}
                </button>
                {merchantApproved && (
                  <button onClick={() => startCashOut(s)} disabled={busy === s.source_id + "-out"}
                          data-testid={`source-cashout-button-${s.source_id}`} title="Move money OUT to your bank — the desk Zelles you"
                          className="rounded-lg border border-amber-500/60 px-4 text-xs font-bold uppercase tracking-widest text-amber-300 disabled:opacity-40">
                    {busy === s.source_id + "-out" ? "…" : "Cash out"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {memo && (
        <div data-testid="funding-memo-card" className="rounded-lg border border-amber-500/40 bg-amber-950/20 p-3 space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-widest text-amber-400">
            Send {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(memo.amount)} by {memo.rail === "ZELLE" ? "Zelle" : "wire"} from your {memo.bank} app
          </p>
          {memo.rail === "ZELLE" && instructions?.zelle?.handle && (
            <button onClick={() => copy(instructions.zelle.handle)} data-testid="memo-copy-handle"
                    className="w-full rounded border border-stone-700 bg-stone-950/60 px-3 py-2 text-left font-mono text-sm text-stone-100">
              to: {instructions.zelle.handle} <span className="text-[9px] text-amber-400">· tap to copy</span>
            </button>
          )}
          <button onClick={() => copy(memo.code)} data-testid="memo-copy-code"
                  className="w-full rounded border border-stone-700 bg-stone-950/60 px-3 py-2 text-left font-mono text-sm tracking-[0.2em] text-amber-300">
            memo: {memo.code} <span className="text-[9px] text-amber-400 tracking-normal">· tap to copy</span>
          </button>
          <p className="text-[9px] text-stone-500 leading-snug">
            Put the memo code in the transfer note. The desk matches it the moment it lands and your wallet credits 1:1 — this panel updates itself.
          </p>
        </div>
      )}

      {msg && <p data-testid="funding-source-msg" className="text-xs text-amber-400/90">{msg}</p>}

      {adding ? (
        <form onSubmit={addSource} className="space-y-2" data-testid="funding-source-form">
          <div className="grid grid-cols-2 gap-2">
            <select value={bank} onChange={(e) => setBank(e.target.value)} data-testid="source-bank-select"
                    className="rounded-lg border border-stone-700 bg-stone-950/70 px-3 py-2 text-sm text-stone-100">
              {BANKS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <input value={last4} onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
                   placeholder="last 4 (optional)" inputMode="numeric" data-testid="source-last4-input"
                   className="rounded-lg border border-stone-700 bg-stone-950/70 px-3 py-2 text-sm text-stone-100" />
          </div>
          {bank === "Other" && (
            <input value={customBank} onChange={(e) => setCustomBank(e.target.value)} required
                   placeholder="Bank name" data-testid="source-custom-bank"
                   className="w-full rounded-lg border border-stone-700 bg-stone-950/70 px-3 py-2 text-sm text-stone-100" />
          )}
          <div className="grid grid-cols-2 gap-2">
            {["ZELLE", "WIRE"].map((r) => (
              <button type="button" key={r} onClick={() => setRail(r)} data-testid={`source-rail-${r.toLowerCase()}`}
                      className={`rounded-lg border py-2 text-[10px] font-mono uppercase tracking-widest ${rail === r ? "border-amber-500/70 bg-amber-950/30 text-amber-300" : "border-stone-700 text-stone-400"}`}>
                {r === "ZELLE" ? "Zelle (instant)" : "Wire / ACH"}
              </button>
            ))}
          </div>
          <p className="text-[9px] text-stone-500 leading-snug">
            Only the label and last four are kept — never your card number. The money moves inside your own bank's app; nothing new to sign up for.
          </p>
          <div className="flex gap-2">
            <button type="submit" disabled={busy === "add"} data-testid="source-add-submit"
                    className="flex-1 rounded-lg bg-amber-500 py-2.5 text-xs font-bold uppercase tracking-widest text-stone-950 disabled:opacity-40">
              {busy === "add" ? "Linking…" : "Link it"}
            </button>
            <button type="button" onClick={() => setAdding(false)} data-testid="source-add-cancel"
                    className="rounded-lg border border-stone-700 px-4 text-xs text-stone-400">Cancel</button>
          </div>
        </form>
      ) : (
        <button onClick={() => { setAdding(true); setMsg(""); }} data-testid="add-funding-source-button"
                className="w-full rounded-lg border border-dashed border-stone-600 py-3 text-xs font-bold uppercase tracking-widest text-stone-300 hover:border-amber-500/60 hover:text-amber-300 transition-colors">
          + Add a bank card
        </button>
      )}
    </div>
  );
};
